import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import { Subscription } from 'rxjs';
import { Supplier } from '../../../shared/interfaces/pricing.interface';

import { SurveyMetaDataService } from '../../../../shared/services/survey-meta-data/survey-meta-data.service';
import { EditSupplierModalService } from '../../edit-supplier-modal.service';
import { CpiMetadata } from '../../../../shared/interfaces/cpi.interface';
import {
  BuyerSurveyResponse,
  GetSupplierBlendInterface,
  SupplierResponseInterface,
} from '../../../../shared/interfaces/survey.interface';
import { FlexibilityPricesParams } from '../../../../shared/interfaces/supplier-api.interface';
import { deepCopy } from '../../../utils/deep-copy';
import { LoaderService } from '@purespectrum1/ui/loader';
import { UpdateSupplierPayloadInterface } from '../../../../shared/interfaces/survey-meta-data.interface';
import { Snapshot } from '../../../shared/interfaces/snapshot';
import { EmptySnapshot } from './domain/empty-snapshot';
import { filter } from 'rxjs/operators';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { CpiService } from '../../../services/cpi/cpi.service';
import { SupplierUpdateSnapshot } from './domain/supplier-update-snapshot';
import { notifyMessage } from '../../../../shared/constants/notify-message';

@Component({
  selector: 'ps-ui-edit-suppliers-modal',
  templateUrl: './edit-suppliers-modal.component.html',
  styleUrls: ['./edit-suppliers-modal.component.css'],
})
export class EditSuppliersModalComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private _subscriptions = new Subscription();

  public data: ModalData = this._modalData;
  public supplier: Snapshot<UpdateSupplierPayloadInterface> =
    new EmptySnapshot();
  public eligibleSuppliers = this.data.eligibleSuppliers;
  public suppliers = deepCopy(this.data.suppliers);
  public readonly flexibilityPrice = this.data.flexibilityPrice;
  public readonly surveyDetails = this.data.surveyDetails;
  public readonly operatorOverrideCPI = this.data.operatorOverrideCPI;
  public readonly selectivePriceOveride = this.data.selectivePriceOveride;
  public readonly isPurePriceApplied = this.data.isPurePriceApplied;
  public readonly supplierBlendData = this.data.supplierBlendData;
  private _isServiceBuyerOperation = this.data.isServiceBuyerOperation;

  private _isCpiOverrideConfirmed = true;
  private _avgCpiThreshold = 0;
  private _manuallyEditedAllocation: boolean = false;
  private _isPurePriceRateCardsEnable: boolean = false;
  constructor(
    @Inject(ModalData)
    private _modalData: ModalData,
    private _modalRef: ModalRef<EditSuppliersModalComponent, string>,
    private _surveyMetaDataService: SurveyMetaDataService,
    private _editSupplierModalService: EditSupplierModalService,
    private _toastrService: ToasterService,
    private _cpiService: CpiService,
    private _loading: LoaderService
  ) {}

  ngOnInit(): void {
    this._subscriptions.add(
      this._surveyMetaDataService
        .getSupplierListPayload()
        .pipe(filter((value) => value && Object.keys(value).length > 0))
        .subscribe((supplier) => {
          this._updateSupplierSelection(supplier);
        })
    );
    this._subscriptions.add(
      this._surveyMetaDataService
        .getSupplierOverrideValue()
        .subscribe((manualScpiOverrideEnabled) => {
          this._isServiceBuyerOperation = !!manualScpiOverrideEnabled;
        })
    );
    this._subscriptions.add(
      this._surveyMetaDataService.getCPIData().subscribe(({ suppliers }) => {
        if (!suppliers) {
          return;
        }
        suppliers = this._mapSupplierGroupingCpis(suppliers);
        this.suppliers = suppliers;
      })
    );

    this._subscriptions.add(
      this._cpiService.cpiVisibility
        .pipe(filter((state) => state))
        .subscribe(() => {
          this._modalRef.close();
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getManuallyEditedAllocation()
        .subscribe((state) => {
          this._manuallyEditedAllocation = state;
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getPurePriceRateCardEnable()
        .subscribe((purePriceRateCardEnable) => {
          this._isPurePriceRateCardsEnable = purePriceRateCardEnable;
        })
    );
  }

  ngAfterViewInit() {
    // react on modal closed by clicking outside it
    this._modalRef.onClose$.subscribe(() => {
      this.supplier.restore();
    });
  }

  private _updateSupplierSelection(
    supplier: UpdateSupplierPayloadInterface
  ): void {
    if (!this.supplier.exists()) {
      return this._takeSupplierSnapshot(supplier);
    }

    if (!this.supplier.equal(supplier)) {
      this.supplier.restore();
      return this._takeSupplierSnapshot(supplier);
    }

    this.supplier.update(supplier);
  }

  private _takeSupplierSnapshot(
    supplier: UpdateSupplierPayloadInterface
  ): void {
    this.supplier = new SupplierUpdateSnapshot(supplier);
    this.supplier.take();
  }

  private _mapSupplierGroupingCpis(suppliers: SupplierResponseInterface[]) {
    if (this.eligibleSuppliers) {
      for (const supplier of suppliers) {
        if (supplier.group_ref && !supplier.cpi) {
          let cpiAmt = 0;
          for (const groupedSupplier of supplier.suppliers) {
            const eligibleSupp = this.eligibleSuppliers.filter(
              (eligibleSupplier) => eligibleSupplier.id === groupedSupplier.id
            );
            cpiAmt += eligibleSupp[0].cpi;
          }
          supplier.cpi = cpiAmt / supplier.suppliers.length;
        }
      }
    }
    return suppliers;
  }

  onCpiOverrideConfirmationChange(state: boolean) {
    this._isCpiOverrideConfirmed = state;
  }

  onAvgCpiThreshold(value: number) {
    this._avgCpiThreshold = value;
  }

  onSupplierRates(value: boolean) {
    this._isPurePriceRateCardsEnable = !value;
  }

  updateSupplier() {
    if (!this._isCpiOverrideConfirmed) {
      this._toastrService.error(notifyMessage.CPI_CONFIRMATION_ERROR);
      return;
    }

    if (this.supplier.exists()) {
      const supplier = this.supplier.get();
      const thresholdWarning =
        supplier.surveyPauseThreshold < this._avgCpiThreshold;

      supplier.manualScpiOverrideEnabled = this._isServiceBuyerOperation;
      supplier.isPurePriceRateCardEnable = this._isPurePriceRateCardsEnable;

      this._editSupplierModalService
        .updateSuppliers(this.data.surveyId, supplier, thresholdWarning)
        .then((isUpdated) => {
          if (this._manuallyEditedAllocation) {
            this._surveyMetaDataService.updateManuallyEditedAllocationSaved(
              true
            );
          }
          if (isUpdated) {
            this.surveyDetails.blend_id = supplier.blend_id;
            this._modalRef.close();
          }
        });
    }
  }

  close(msg?: string) {
    this.supplier.restore();
    this._modalRef.close(msg);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}

interface ModalData {
  isServiceBuyerOperation: boolean;
  eligibleSuppliers: Supplier[];
  surveyId: number;
  metadata: CpiMetadata;
  suppliers: SupplierResponseInterface[];
  surveyDetails: BuyerSurveyResponse;
  operatorOverrideCPI?: string;
  selectivePriceOveride?: boolean;
  flexibilityPrice?: FlexibilityPricesParams;
  supplierBlendData?: GetSupplierBlendInterface[];
  isPurePriceApplied?: boolean;
  isInsights?: boolean;
}
